import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { show, hide, visible } from '@shared/easyfy.utils';
import userService from '@webServices/user.service';
import { getDialogManager } from '@webModules/DialogManager';
import { Backdrop } from '@webModules/backdrop';
import Favourites from '@webModules/favourites';
import MedsBasket from '@webModules/medsBasket';
import basketModule from '@webModules/basket';
import { setMyPagesMenuState } from './myPagesMenuState';

// Singleton
const dm = getDialogManager();

namespace Meds.Modules {

  @invoke
  class Header {
    onInit() {
      // get auth user
      this.GetAuthenticatedUser();

      globalEvent({
        type: 'click',
        events: [
          {
            event: 'toggle-main-menu',
            callback: Header.onToggleMainMenu
          }
        ]
      });

      globalEvent({
        type: 'click',
        event: {
          event: 'toggle-prescription-menu',
          callback: Header.onTogglePrescriptionMenu
        }
      });

      globalEvent({
        type: 'click',
        events: [
          {
            event: 'toggle-mypages-menu',
            callback: Header.onToggleMyPagesMenu
          }
        ]
      });

    }

    /** Opens or closes the main navigation menu and manages the backdrop accordingly. */
    static onToggleMainMenu = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const menu: HTMLElement = document.querySelector('[data-container="main-menu"]');
        if (!menu) return;

        if (visible(menu)) {
          hide(menu);
          Backdrop.hide();
        } else {
          show(menu);
          Backdrop.show('backdrop-page');
          Backdrop.addClickListener(() => {
            hide(menu);
            Backdrop.hide();
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    /** Closes the mini-basket when a click occurs outside of it. */
    static hideMiniBasket = async (e: Event) => {

      try {
        const miniBasket = document.querySelector<HTMLElement>('[data-container="mini-basket"]');
        if (miniBasket === null) {
          return;
        }

        if (miniBasket instanceof HTMLDialogElement) {
          // The miniBasket is a dialog element, so we can use a simplified way to close it.
          miniBasket.close();
        } else {
          // The minibasket is not a dialog element, so we need a more complex way to open and close it.
          const miniBasketMenuButton = document.querySelector<HTMLElement>('[data-target="mini-basket"]');
          const clickedElement = e.target as HTMLElement;
          const miniBasketHasBeenClicked: boolean = miniBasket.contains(clickedElement) || miniBasket === clickedElement;

          if (!visible(miniBasket)) {
            return;
          }

          if (miniBasketMenuButton.contains(clickedElement)) {
            return;
          }

          if (miniBasketHasBeenClicked) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          MedsBasket.MedsBasket.onCloseBasket(e);
        }
      } catch (error) {
        console.error(error);
      }
    };

    /** Opens or closes the mini-basket and manages the backdrop accordingly. */
    static onToggleMiniBasket = async (e: Event) => {

      e.preventDefault();
      e.stopPropagation();

      try {
        let targetElement = e.target as HTMLElement;

        if (targetElement.getAttribute('data-target') == null) {
          targetElement = targetElement.closest('[data-target="mini-basket"]');
        }

        const menu: HTMLElement = document.querySelector(`[data-container="${targetElement.attributes['data-target']?.value}"]`);
        if (menu instanceof HTMLDialogElement) {
          // The menu is a dialog element, so we can use a simplified way to open and close it.
          if (!menu.open) {
            menu.showModal();
          } else {
            menu.close();
          }
        } else {
          // The minibasket is not a dialog element, so we need a more complex way to open and close it.
          const basketOverlay: HTMLElement = document.querySelector(
            '[data-container="mini-basket"]'
          );

          if (visible(basketOverlay)) {
            Backdrop.hide();
          } else {
            Backdrop.show('backdrop-page');
          }
          Header.toggleMenuVisibility(e, targetElement, menu);
        }


      } catch (error) {
        console.error(error);
      }
    };

    static onTogglePrescriptionMenu = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const targetElement: HTMLElement =
          (e.target as HTMLElement).getAttribute('data-target') != null
            ? (e.target as HTMLElement)
            : (e.target as HTMLElement).closest(
              '[data-target="top-menu-prescription"]'
            );
        const parentElement: HTMLElement = targetElement.closest(
          '[data-container="header-prescription-container"]'
        );
        const menu: HTMLElement = document.querySelector(
          `[data-container="${targetElement.attributes['data-target']?.value}"]`
        );

        Header.toggleMenuVisibility(e, parentElement, menu, true);
      } catch (error) {
        console.error(error);
      }
    };

    static onToggleMyPagesMenu = async (e: Event) => {

      e.preventDefault();
      e.stopPropagation();

      try {
        const targetElement: HTMLElement =
          (e.target as HTMLElement).getAttribute('data-target') != null
            ? (e.target as HTMLElement)
            : (e.target as HTMLElement).closest(
              '[data-target="top-menu-mypages"]'
            );
        const parentElement: HTMLElement = targetElement.closest(
          '[data-container="header-mypages-container"]'
        );
        const menu: HTMLElement = document.querySelector(
          `[data-container="${targetElement.attributes['data-target']?.value}"]`
        );

        const loginNode = menu?.querySelector<HTMLElement>('.nav-node-login');
        const nav = menu?.querySelector<HTMLElement>('.my-pages-navigation');
        if (loginNode) { hide(loginNode); }
        if (nav) { show(nav); }

        Header.toggleMenuVisibility(e, parentElement, menu, true);
      } catch (error) {
        console.error(error);
      }
    };

    /**
     * Shows or hides a header menu element. Handles both `<dialog>` and non-dialog elements.
     * @param targetElement The container element that receives the `menu-isShown` class.
     * @param menu The menu element to show or hide.
     * @param toggleBackdrop Whether to show/hide the backdrop alongside the menu.
     */
    private static toggleMenuVisibility(
      e: Event,
      targetElement: HTMLElement,
      menu: HTMLElement,
      toggleBackdrop: boolean = false
    ) {

      try {
        // Dialogs have built-in modal behavior
        if (menu instanceof HTMLDialogElement) {
          if (menu.open) {
            menu.close();
          } else {
            menu.showModal();
          }
          return;
        }

        // Non-dialog fallback case
        const btn = targetElement.querySelector<HTMLButtonElement>('button');
        const setExpanded = (isExpanded: boolean) => {
          if (btn?.hasAttribute('aria-expanded')) {
            btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
          }
        };

        const closeMenu = () => {
          targetElement.classList.remove('menu-isShown');
          setExpanded(false);
          hide(menu);
          if (toggleBackdrop) {
            Header.hideBackdrop(e);
          }
        };

        const openMenu = () => {
          targetElement.classList.add('menu-isShown');
          setExpanded(true);
          show(menu);
          if (toggleBackdrop) {
            Header.showBackdrop(e);
            Header.hideMenuEventListener(e, targetElement, menu);
          }
        };

        const isOpen = targetElement.classList.contains('menu-isShown');
        if (isOpen) {
          closeMenu();
        } else {
          openMenu();
        }

      } catch (error) {
        console.error(error);
      }
    }

    /** Shows the backdrop with the `backdrop-content` modifier. */
    private static showBackdrop(_e: Event) {

      try {
        Backdrop.show('backdrop-content');
      } catch (error) {
        console.error(error);
      }
    }

    /** Hides the backdrop, unless another menu is still open. */
    public static hideBackdrop(e: Event) {
      try {
        const openMenu = document.querySelector(".menu-isShown") as HTMLElement;
        if (e?.target && !openMenu) {
          Backdrop.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }

    /**
     * Attaches a one-time outside-click listener to both the backdrop and the header,
     * closing the given menu when a click outside `menuContainer` is detected.
     */
    static hideMenuEventListener = (
      e: Event,
      menuContainer: HTMLElement,
      menuElement: HTMLElement
    ) => {

      try {
        const handleOutsideClick = (evt: Event) => {
          const target = evt.target as HTMLElement | null;
          if (target && !menuContainer.contains(target)) {
            menuContainer.classList.remove("menu-isShown");

            const button = menuContainer.querySelector("button");
            if (button?.hasAttribute("aria-expanded")) {
              button.setAttribute("aria-expanded", "false");
            }
            hide(menuElement);
            Header.hideBackdrop(evt);
          }
        };

        Backdrop.addClickListener(handleOutsideClick);

        const headerContainer = document.querySelector('.header-container') as HTMLElement;
        headerContainer?.addEventListener('click', handleOutsideClick, { once: true });
      } catch (error) {
        console.error(error);
      }
    };

    /** Fetches the authenticated user and updates the header UI accordingly. */
    private GetAuthenticatedUser = async () => {

      const destinationElement = document.getElementById(
        'header-authenticated-wrapper'
      );

      if (destinationElement == null) {
        return;
      }

      userService
        .GetAuthenticatedUser()
        .then((response) => {
          if (
            response.status === 200 &&
            response.data != null &&
            response.data.isLoggedIn
          ) {
            // replace the content if the user is logged in
            destinationElement.innerHTML = response.data.partialUI;

            // ensure the login form is hidden and favourite icon shown in the nav menu
            setMyPagesMenuState(false);

            const myPagesMenu = document.querySelector<HTMLElement>('#top-menu-mypages');
            if (myPagesMenu) {
              myPagesMenu.dataset.authenticated = 'true';
            }

            Favourites.Favourites.renderFavourites(response.data.favouritesCount, response.data.favouritesProductId);

            if (response.data.isNewLogin) {
              basketModule.Basket.SyncBasketFromCartAndValidatePrices();
            }

          } else {
            // ensure the login form is shown correct in the nav menu
            show('.nav-node-login');

            document.querySelectorAll<HTMLElement>('.logout-form').forEach(el => hide(el));
          }
        })
        .catch((error) => {
          console.error(error);
        });
    };
  }

}
